
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { auditLogService, IAuditLogAuthor } from '@/services/auditLogService';

const SEARCH_MIN_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Picks the author ("changed by") of audit log entries by name or email,
 * among the users that actually authored an entry.
 */
@Component({
  name: 'AuditLogAuthorFilter',
})
export default class AuditLogAuthorFilter extends Vue {
  @Prop({ default: null })
  protected value!: IAuditLogAuthor | null;

  protected results: IAuditLogAuthor[] = [];
  protected search: string | null = null;
  protected loading: boolean = false;
  protected searched: boolean = false;
  private timer: any = null;

  // The selected author must stay among the items or v-autocomplete drops its label.
  get items(): IAuditLogAuthor[] {
    if (this.value && !this.results.some(author => author.id === this.value!.id)) {
      return [this.value].concat(this.results);
    }

    return this.results;
  }

  get hint(): string {
    if (this.loading) {
      return this.$t('common.loading') as string;
    }
    if (!this.searched) {
      return this.$t('auditLogs.filters.authorTooShort') as string;
    }

    return this.$t('auditLogs.filters.authorNoResults') as string;
  }

  @Watch('search')
  onSearch(search: string | null) {
    if (this.timer) {
      clearTimeout(this.timer);
    }
    // Typing is echoed back as the selected label once an author is picked: don't search for it.
    if (!search || search.trim().length < SEARCH_MIN_LENGTH || (this.value && search === this.authorName(this.value))) {
      this.searched = false;
      return;
    }
    this.timer = setTimeout(() => this.fetch(search.trim()), SEARCH_DEBOUNCE_MS);
  }

  authorName(author: IAuditLogAuthor): string {
    const name = [author.first_name, author.last_name].filter(part => !!part).join(' ').trim();

    return name || author.email || '#' + author.id;
  }

  initials(author: IAuditLogAuthor): string {
    return this.authorName(author).replace('#', '').substring(0, 1).toUpperCase();
  }

  avatarStyle(author: IAuditLogAuthor): object {
    const hue = (author.id * 47) % 360;

    return {
      backgroundColor: 'hsl(' + hue + ', 45%, 90%)',
      color: 'hsl(' + hue + ', 45%, 30%)',
    };
  }

  // On focus Vuetify fills the input with the selected author's name: select it so typing replaces it
  // instead of appending to it (which would search for "Ana Garcíamig").
  selectSearchText(event: FocusEvent) {
    const input = event && (event.target as HTMLInputElement);
    if (!input || typeof input.select !== 'function') {
      return;
    }
    // a mouse focus is followed by a mouseup that would move the caret and drop the selection
    input.addEventListener('mouseup', (mouseup: MouseEvent) => mouseup.preventDefault(), { once: true });
    setTimeout(() => {
      // only the name Vuetify put there: never select what the user already started typing
      if (this.value && input.value === this.authorName(this.value)) {
        input.select();
      }
    });
  }

  onChange(author: IAuditLogAuthor | null) {
    this.$emit('input', author || null);
  }

  private async fetch(search: string) {
    this.loading = true;
    try {
      this.results = await auditLogService.searchAuthors(search);
      this.searched = true;
    } finally {
      this.loading = false;
    }
  }
}
